import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { mediaList } from "@/mocks/media";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useSearchHistory } from "@/hooks/useLocalLibrary";
import { collectYears, searchMedia, sortMedia, typeLabelMap } from "@/utils/media";
import MediaCard from "@/components/feature/MediaCard";
import EmptyState from "@/components/base/EmptyState";

const HOT_KEYWORDS = ["悬疑", "治愈", "韩国电影", "日剧", "爱情", "综艺"];
const SORTS = [
  { value: "heat", label: "热度" },
  { value: "rating", label: "评分" },
  { value: "latest", label: "最新" },
];

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = searchParams.get("q") || "";
  const [input, setInput] = useState(keyword);
  const [focused, setFocused] = useState(false);
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("heat");
  const { keywords, addKeyword, clearKeywords } = useSearchHistory();

  useEffect(() => {
    setInput(keyword);
    setRegion("全部");
    setYear("全部");
    setSort("heat");
  }, [keyword]);

  useDocumentMeta(
    keyword ? `“${keyword}”的搜索结果 - 日韩影视大全` : "搜索日韩影视作品 - 日韩影视大全",
    keyword
      ? `在日韩影视大全中搜索“${keyword}”，查找相关的日韩电影、电视剧与综艺作品，包含主演、导演与类型信息。`
      : "搜索日韩电影、电视剧与综艺作品。支持按片名、主演、导演与题材关键词检索，并提供分类筛选。",
    "日韩影视搜索,日韩电影搜索,日韩电视剧搜索,影视作品查询",
  );

  const suggestions = useMemo(() => {
    const value = input.trim();
    if (!value) return [];
    return searchMedia(mediaList, value).slice(0, 6);
  }, [input]);

  const results = useMemo(() => {
    let list = keyword.trim() ? searchMedia(mediaList, keyword) : [];
    if (region !== "全部") list = list.filter((item) => item.region === region);
    if (year !== "全部") list = list.filter((item) => String(item.year) === year);
    return sortMedia(list, sort as "heat" | "rating" | "latest");
  }, [keyword, region, year, sort]);

  const recommended = useMemo(() => sortMedia(mediaList, "rating").slice(0, 4), []);
  const years = useMemo(() => collectYears(mediaList).slice(0, 5), []);

  const submit = (value: string) => {
    const trimmed = value.trim();
    addKeyword(trimmed);
    setFocused(false);
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search");
  };

  return (
    <div className="w-full pb-16">
      <section className="w-full border-b border-background-200/80 bg-background-100">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-12">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer transition-colors hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
            <span className="text-foreground-900">站内搜索</span>
          </nav>

          <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            搜索日韩影视作品
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            输入片名、主演或导演，也可以直接用题材关键词，例如「悬疑」「治愈」。
          </p>

          <div className="relative mt-5 max-w-2xl">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                submit(input);
              }}
              className="flex items-center gap-2 rounded-lg border border-background-300 bg-background-50 p-1.5 focus-within:border-primary-400"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-lg" />
              </span>
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => window.setTimeout(() => setFocused(false), 120)}
                placeholder="搜索影视作品、主演、导演"
                aria-label="搜索影视作品、主演、导演"
                className="min-w-0 flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {input && (
                <button
                  type="button"
                  onClick={() => setInput("")}
                  aria-label="清空关键词"
                  className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
                >
                  <i className="ri-close-line text-base" />
                </button>
              )}
              <button
                type="submit"
                className="shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-primary-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
              >
                搜索
              </button>
            </form>

            {focused && suggestions.length > 0 && (
              <div className="absolute inset-x-0 top-full z-20 mt-2 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-1.5">
                <p className="px-2.5 py-1.5 text-[10px] uppercase tracking-[0.18em] text-foreground-400">
                  关键词联想
                </p>
                {suggestions.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      setFocused(false);
                      addKeyword(input.trim());
                      navigate(`/detail/${item.id}`);
                    }}
                    className="flex w-full cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-background-100"
                  >
                    <span className="h-11 w-8 shrink-0 overflow-hidden rounded-sm bg-background-200">
                      <img src={item.cover} alt={item.title} className="h-full w-full object-cover object-top" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-foreground-900">{item.title}</span>
                      <span className="mt-0.5 block truncate text-[11px] text-foreground-500">
                        {item.year} · {item.region} · {item.genres.join(" / ")}
                      </span>
                    </span>
                    <span className="shrink-0 rounded-sm bg-background-100 px-1.5 py-0.5 text-[10px] text-foreground-500">
                      {typeLabelMap[item.type]}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-500">搜索历史</span>
            {keywords.length > 0 ? (
              <>
                {keywords.map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => submit(word)}
                    className="cursor-pointer whitespace-nowrap rounded-full bg-background-50 px-2.5 py-1 text-[11px] text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                  >
                    {word}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={clearKeywords}
                  className="cursor-pointer whitespace-nowrap text-[11px] text-foreground-400 transition-colors hover:text-primary-700"
                >
                  清空历史
                </button>
              </>
            ) : (
              <span className="text-[11px] text-foreground-400">暂无历史记录</span>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-500">热门搜索</span>
            {HOT_KEYWORDS.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => submit(word)}
                className="cursor-pointer whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8">
        {keyword ? (
          <>
            <div className="mt-6 flex flex-col gap-4 border-b border-background-200/80 pb-4">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="text-[11px] text-foreground-500">当前关键词</p>
                  <h2 className="mt-1 font-heading text-lg font-semibold text-foreground-950">
                    “{keyword}”的搜索结果
                  </h2>
                </div>
                <p className="text-xs text-foreground-500">
                  找到 <span className="font-semibold text-primary-700">{results.length}</span> 部相关作品
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                  {["全部", "日本", "韩国"].map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setRegion(item)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] transition-colors ${
                        region === item ? "bg-primary-950 text-background-50" : "text-foreground-600"
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
                <select
                  value={year}
                  onChange={(event) => setYear(event.target.value)}
                  aria-label="按年份筛选"
                  className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-[11px] text-foreground-700 outline-none"
                >
                  <option value="全部">全部年份</option>
                  {years.map((item) => (
                    <option key={item} value={String(item)}>
                      {item} 年
                    </option>
                  ))}
                </select>
                <div className="ml-auto flex items-center gap-1">
                  {SORTS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setSort(option.value)}
                      className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1 text-[11px] transition-colors ${
                        sort === option.value
                          ? "bg-accent-100 font-medium text-accent-900"
                          : "text-foreground-500 hover:text-foreground-900"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6">
              {results.length > 0 ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 xl:grid-cols-4">
                  {results.map((item) => (
                    <MediaCard key={item.id} item={item} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-10">
                  <EmptyState
                    icon="ri-search-eye-line"
                    title="没有找到匹配的作品"
                    description="换一个关键词试试，或者减少筛选条件。也可以从下方的高分推荐开始浏览。"
                  >
                    <button
                      type="button"
                      onClick={() => navigate("/search")}
                      className="cursor-pointer whitespace-nowrap rounded-md bg-primary-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
                    >
                      重置搜索条件
                    </button>
                    <Link
                      to="/category/all"
                      className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                    >
                      浏览全部作品
                    </Link>
                  </EmptyState>

                  <div>
                    <h2 className="font-heading text-lg font-semibold text-foreground-950">高分影视推荐</h2>
                    <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 xl:grid-cols-4">
                      {recommended.map((item) => (
                        <MediaCard key={item.id} item={item} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="mt-8">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                  <i className="ri-lightbulb-line text-base" />
                </span>
                <h2 className="font-heading text-base font-semibold text-foreground-950">搜索小提示</h2>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                可以直接输入作品名称，也可以搜索主演、导演或类型。输入过程中会出现关键词联想，点击即可进入对应详情页。
              </p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {["首尔夜行", "汉江以北", "治愈", "京都", "李在勋", "月见食堂"].map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => submit(word)}
                    className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    {word}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.2em] text-foreground-500">
                    <span className="h-px w-6 bg-accent-500" />
                    Recommended
                  </p>
                  <h2 className="mt-2 font-heading text-xl font-semibold text-foreground-950">高分影视推荐</h2>
                </div>
                <Link
                  to="/category/all"
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-700"
                >
                  查看全部
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 xl:grid-cols-4">
                {sortMedia(mediaList, "rating")
                  .slice(0, 8)
                  .map((item) => (
                    <MediaCard key={item.id} item={item} />
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}